class OfflineModeManager {
  constructor() {
    this.isSupported = false;
    this.initializationError = null;
    this.isInitialized = false;
    this.testDbName = "OfflineModeTest";
  }

  async initialize() {
    console.log('🚀 [Offline Mode Manager] Starting initialization...');
    
    if (this.isInitialized) {
      console.log('ℹ️ [Offline Mode Manager] Already initialized, isSupported:', this.isSupported);
      return this.isSupported;
    }

    try {
      console.log('🔍 [Offline Mode Manager] Checking browser support...');
      if (!this.checkBrowserSupport()) {
        throw new Error("IndexedDB not supported in this browser");
      }
      console.log('✅ [Offline Mode Manager] Browser supports IndexedDB');

      console.log('🧪 [Offline Mode Manager] Testing database operations...');
      await this.testDatabaseOperations();
      console.log('✅ [Offline Mode Manager] Database operations test passed');
      
      this.isSupported = true;
      this.initializationError = null;
      console.log('✅ [Offline Mode Manager] Initialization successful - offline mode ENABLED');
    } catch (error) {
      this.isSupported = false;
      this.initializationError = error.message;
      console.error("❌ [Offline Mode Manager] Initialization failed:", error.message);
      console.error("❌ [Offline Mode Manager] Offline mode will be DISABLED");
    } finally {
      this.isInitialized = true;
    }

    console.log('📊 [Offline Mode Manager] Final status:', {
      isInitialized: this.isInitialized,
      isSupported: this.isSupported,
      error: this.initializationError
    });

    return this.isSupported;
  }

  checkBrowserSupport() {
    if (!window.indexedDB) {
      return false;
    }

    if (typeof indexedDB.open !== "function") {
      return false;
    }

    return true;
  }

  async testDatabaseOperations() {
    return new Promise((resolve, reject) => {
      const testKey = "test-key-" + Date.now();
      const testValue = { data: "test", timestamp: Date.now() };

      const request = indexedDB.open(this.testDbName, 1);
      let db;

      request.onerror = () => {
        reject(new Error("Failed to open test database"));
      };

      request.onblocked = () => {
        reject(new Error("Database open blocked"));
      };

      request.onupgradeneeded = (event) => {
        const database = event.target.result;
        if (!database.objectStoreNames.contains("testStore")) {
          database.createObjectStore("testStore");
        }
      };

      request.onsuccess = (event) => {
        db = event.target.result;

        const transaction = db.transaction(["testStore"], "readwrite");
        const store = transaction.objectStore("testStore");

        const putRequest = store.put(testValue, testKey);

        putRequest.onerror = () => {
          db.close();
          reject(new Error("Failed to write test data"));
        };

        putRequest.onsuccess = () => {
          const getRequest = store.get(testKey);

          getRequest.onerror = () => {
            db.close();
            reject(new Error("Failed to read test data"));
          };

          getRequest.onsuccess = () => {
            const result = getRequest.result;
            
            if (!result || result.data !== testValue.data) {
              db.close();
              reject(new Error("Data verification failed"));
              return;
            }

            const deleteRequest = store.delete(testKey);
            
            deleteRequest.onsuccess = () => {
              db.close();
              this.cleanupTestDatabase();
              resolve();
            };

            deleteRequest.onerror = () => {
              db.close();
              this.cleanupTestDatabase();
              resolve();
            };
          };
        };

        transaction.onerror = () => {
          db.close();
          reject(new Error("Transaction failed"));
        };
      };
    });
  }

  cleanupTestDatabase() {
    try {
      indexedDB.deleteDatabase(this.testDbName);
    } catch (error) {
      console.warn("Failed to cleanup test database:", error);
    }
  }

  async checkIndexedDBHealth() {
    if (!this.isInitialized) {
      await this.initialize();
    }
    return this.isSupported;
  }

  isOfflineModeSupported() {
    return this.isSupported;
  }

  getInitializationError() {
    return this.initializationError;
  }

  getErrorMessage() {
    if (this.isSupported) {
      return null;
    }
    
    if (this.initializationError) {
      return `Offline mode unavailable: ${this.initializationError}`;
    }
    
    return "Offline mode unavailable: IndexedDB not supported";
  }

  getUserFriendlyMessage() {
    if (this.isSupported) {
      return null;
    }
    
    return "Offline mode is unavailable. You must be online to submit each answer.";
  }
}

export const offlineModeManager = new OfflineModeManager();

